import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import BoxedTitle from "@/components/BoxedTitle";
import useAdminReferences from "@/pages/admin/references/useAdminReferences";
import { clearCache } from "@/pages/admin/adminCache";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "vbb_admin_key";

const CSL_TYPES = [
  { id: "webpage", label: "Webpage" },
  { id: "book", label: "Book" },
  { id: "chapter", label: "Book chapter" },
  { id: "article-journal", label: "Journal article" },
  { id: "article-magazine", label: "Magazine article" },
  { id: "article-newspaper", label: "News article" },
  { id: "motion_picture", label: "Video / film" },
  { id: "interview", label: "Interview" },
  { id: "post-weblog", label: "Blog post" },
  { id: "speech", label: "Speech / talk" },
];

const emptyAuthor = () => ({
  _uid: `a_${Math.random().toString(36).slice(2, 10)}`,
  family: "",
  given: "",
});

const blankForm = () => ({
  type: "webpage",
  title: "",
  authors: [emptyAuthor()],
  year: "",
  containerTitle: "",
  publisher: "",
  url: "",
  doi: "",
  isbn: "",
  page: "",
  volume: "",
  issue: "",
  notes: "",
  fullText: "",
  published: false,
});

function cslToForm(csl, notes, published, fullText) {
  const c = csl || {};
  const year = (() => {
    const parts = c.issued?.["date-parts"];
    if (Array.isArray(parts) && parts[0]?.[0]) return String(parts[0][0]);
    return "";
  })();
  return {
    type: c.type || "webpage",
    title: c.title || "",
    authors:
      Array.isArray(c.author) && c.author.length > 0
        ? c.author.map((a) => ({
            _uid: `a_${Math.random().toString(36).slice(2, 10)}`,
            family: a.family || "",
            given: a.given || "",
          }))
        : [emptyAuthor()],
    year,
    containerTitle: c["container-title"] || "",
    publisher: c.publisher || "",
    url: c.URL || c.url || "",
    doi: c.DOI || "",
    isbn: c.ISBN || "",
    page: c.page || "",
    volume: c.volume || "",
    issue: c.issue || "",
    notes: notes || "",
    fullText: fullText || "",
    published: !!published,
  };
}

function formToPayload(form) {
  const csl = {
    type: form.type,
    title: form.title.trim(),
  };
  const authors = form.authors
    .map((a) => ({
      family: (a.family || "").trim(),
      given: (a.given || "").trim(),
    }))
    .filter((a) => a.family || a.given);
  if (authors.length) csl.author = authors;
  if (form.year && /^\d{3,4}$/.test(form.year.trim())) {
    csl.issued = { "date-parts": [[parseInt(form.year, 10)]] };
  }
  if (form.containerTitle.trim()) csl["container-title"] = form.containerTitle.trim();
  if (form.publisher.trim()) csl.publisher = form.publisher.trim();
  if (form.url.trim()) csl.URL = form.url.trim();
  if (form.doi.trim()) csl.DOI = form.doi.trim();
  if (form.isbn.trim()) csl.ISBN = form.isbn.trim();
  if (form.page.trim()) csl.page = form.page.trim();
  if (form.volume.trim()) csl.volume = form.volume.trim();
  if (form.issue.trim()) csl.issue = form.issue.trim();
  return {
    csl,
    notes: form.notes,
    full_text: form.fullText,
    published: form.published,
  };
}

/**
 * /admin/references/new + /admin/references/:id
 * Structured form over CSL-JSON. Full CSL fields available; blank
 * fields simply drop out of the stored dict.
 */
export default function AdminReferenceEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { key, createReference, patchReference } = useAdminReferences();

  const [loaded, setLoaded] = useState(isNew);
  const [savedState, setSavedState] = useState("idle");
  const [preview, setPreview] = useState({ apa: "", mla: "" });
  const [form, setForm] = useState(blankForm);

  useEffect(() => {
    if (!key) {
      navigate("/login?next=/admin/references", { replace: true });
      return;
    }
    if (isNew) return;
    let cancelled = false;
    axios
      .get(`${API}/admin/references/${id}`, {
        headers: { "X-Admin-Key": key },
      })
      .then((res) => {
        if (cancelled) return;
        setForm(
          cslToForm(
            res.data.csl,
            res.data.notes,
            res.data.published,
            res.data.full_text
          )
        );
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setSavedState("error");
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, key, navigate]);

  // Live citation preview — hits both endpoints when we have a saved id.
  useEffect(() => {
    if (isNew || !id) {
      setPreview({ apa: "", mla: "" });
      return;
    }
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/admin/references/${id}/format?style=apa`, {
        headers: { "X-Admin-Key": key },
      }),
      axios.get(`${API}/admin/references/${id}/format?style=mla`, {
        headers: { "X-Admin-Key": key },
      }),
    ])
      .then(([apa, mla]) => {
        if (cancelled) return;
        setPreview({ apa: apa.data.citation, mla: mla.data.citation });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [id, isNew, key, savedState]);

  const onLock = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    clearCache();
    navigate("/login?next=/admin/references", { replace: true });
  };

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));

  const setAuthor = (index, patch) =>
    setForm((f) => ({
      ...f,
      authors: f.authors.map((a, i) => (i === index ? { ...a, ...patch } : a)),
    }));

  const addAuthor = () =>
    setForm((f) => ({ ...f, authors: [...f.authors, emptyAuthor()] }));

  const removeAuthor = (index) =>
    setForm((f) => ({
      ...f,
      authors: f.authors.length > 1
        ? f.authors.filter((_, i) => i !== index)
        : [emptyAuthor()],
    }));

  const onSave = async () => {
    if (!form.title.trim()) {
      setSavedState("error");
      return;
    }
    setSavedState("saving");
    const payload = formToPayload(form);
    try {
      if (isNew) {
        const created = await createReference(payload);
        setSavedState("saved");
        navigate(`/admin/references/${created.id}`, { replace: true });
        return;
      }
      await patchReference(id, payload);
      setSavedState("saved");
      setTimeout(() => setSavedState("idle"), 1800);
    } catch {
      setSavedState("error");
    }
  };

  if (!key || !loaded) return null;

  return (
    <main
      className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-32"
      data-testid="admin-reference-edit-page"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="vbb-overline">
            <Link to="/admin" className="vbb-link" data-testid="crumb-admin">
              Admin
            </Link>{" "}
            /{" "}
            <Link
              to="/admin/references"
              className="vbb-link"
              data-testid="crumb-references"
            >
              References
            </Link>{" "}
            / {isNew ? "New" : "Edit"}
          </div>
          <h1
            className="font-display uppercase text-4xl sm:text-5xl mt-6 tracking-tighter leading-[0.95] pl-[0.35em]"
            data-testid="admin-reference-edit-title"
          >
            <BoxedTitle size="lg" thick>
              {isNew ? "New" : "Edit"}
            </BoxedTitle>{" "}
            reference.
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/references"
            className="vbb-btn vbb-btn-ghost"
            data-testid="reference-back-btn"
          >
            ← References
          </Link>
          <button
            onClick={onLock}
            className="vbb-btn vbb-btn-ghost"
            data-testid="admin-lock-btn"
          >
            Lock
          </button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          {/* Type */}
          <div>
            <label htmlFor="ref-type" className="vbb-overline block mb-2">
              Type
            </label>
            <select
              id="ref-type"
              value={form.type}
              onChange={(e) => setField({ type: e.target.value })}
              className="w-full border border-[#0a0a0a] p-3 text-[14px] bg-white focus:outline-none focus:border-[#e34234]"
              data-testid="ref-type-select"
            >
              {CSL_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="ref-title" className="vbb-overline block mb-2">
              Title *
            </label>
            <input
              id="ref-title"
              type="text"
              value={form.title}
              onChange={(e) => setField({ title: e.target.value })}
              className="w-full border border-[#0a0a0a] p-3 text-[16px] focus:outline-none focus:border-[#e34234]"
              data-testid="ref-title-input"
            />
          </div>

          {/* Authors */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="vbb-overline block">Authors</label>
              <button
                type="button"
                onClick={addAuthor}
                className="font-mono-tiny vbb-link"
                data-testid="ref-add-author-btn"
              >
                + Add author
              </button>
            </div>
            <div className="space-y-2">
              {form.authors.map((author, i) => (
                <div
                  key={author._uid || i}
                  className="flex items-center gap-2"
                  data-testid={`ref-author-row-${i}`}
                >
                  <input
                    type="text"
                    value={author.given}
                    onChange={(e) => setAuthor(i, { given: e.target.value })}
                    placeholder="Given (first) name"
                    className="flex-1 border border-[#0a0a0a] p-2 text-[14px] focus:outline-none focus:border-[#e34234]"
                    data-testid={`ref-author-given-${i}`}
                  />
                  <input
                    type="text"
                    value={author.family}
                    onChange={(e) => setAuthor(i, { family: e.target.value })}
                    placeholder="Family (last) name"
                    className="flex-1 border border-[#0a0a0a] p-2 text-[14px] focus:outline-none focus:border-[#e34234]"
                    data-testid={`ref-author-family-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeAuthor(i)}
                    className="px-3 py-2 font-mono-tiny text-[#e34234] hover:bg-[#fafafa]"
                    data-testid={`ref-author-remove-${i}`}
                    aria-label="Remove author"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="ref-year" className="vbb-overline block mb-2">
                Year
              </label>
              <input
                id="ref-year"
                type="text"
                value={form.year}
                onChange={(e) => setField({ year: e.target.value })}
                placeholder="2024"
                className="w-full border border-[#0a0a0a] p-2 text-[14px] focus:outline-none focus:border-[#e34234]"
                data-testid="ref-year-input"
              />
            </div>
            <div>
              <label htmlFor="ref-page" className="vbb-overline block mb-2">
                Page(s)
              </label>
              <input
                id="ref-page"
                type="text"
                value={form.page}
                onChange={(e) => setField({ page: e.target.value })}
                placeholder="12–34"
                className="w-full border border-[#0a0a0a] p-2 text-[14px] focus:outline-none focus:border-[#e34234]"
                data-testid="ref-page-input"
              />
            </div>
            <div>
              <label htmlFor="ref-volume" className="vbb-overline block mb-2">
                Vol / Issue
              </label>
              <div className="flex gap-2">
                <input
                  id="ref-volume"
                  type="text"
                  value={form.volume}
                  onChange={(e) => setField({ volume: e.target.value })}
                  placeholder="Vol"
                  className="w-1/2 border border-[#0a0a0a] p-2 text-[14px] focus:outline-none focus:border-[#e34234]"
                  data-testid="ref-volume-input"
                />
                <input
                  type="text"
                  value={form.issue}
                  onChange={(e) => setField({ issue: e.target.value })}
                  placeholder="Iss"
                  className="w-1/2 border border-[#0a0a0a] p-2 text-[14px] focus:outline-none focus:border-[#e34234]"
                  data-testid="ref-issue-input"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="ref-container"
                className="vbb-overline block mb-2"
              >
                Container (channel / journal / book series)
              </label>
              <input
                id="ref-container"
                type="text"
                value={form.containerTitle}
                onChange={(e) => setField({ containerTitle: e.target.value })}
                className="w-full border border-[#0a0a0a] p-2 text-[14px] focus:outline-none focus:border-[#e34234]"
                data-testid="ref-container-input"
              />
            </div>
            <div>
              <label
                htmlFor="ref-publisher"
                className="vbb-overline block mb-2"
              >
                Publisher / platform
              </label>
              <input
                id="ref-publisher"
                type="text"
                value={form.publisher}
                onChange={(e) => setField({ publisher: e.target.value })}
                className="w-full border border-[#0a0a0a] p-2 text-[14px] focus:outline-none focus:border-[#e34234]"
                data-testid="ref-publisher-input"
              />
            </div>
          </div>

          <div>
            <label htmlFor="ref-url" className="vbb-overline block mb-2">
              URL
            </label>
            <input
              id="ref-url"
              type="text"
              value={form.url}
              onChange={(e) => setField({ url: e.target.value })}
              className="w-full border border-[#0a0a0a] p-2 text-[14px] font-mono-tiny focus:outline-none focus:border-[#e34234]"
              data-testid="ref-url-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="ref-doi" className="vbb-overline block mb-2">
                DOI
              </label>
              <input
                id="ref-doi"
                type="text"
                value={form.doi}
                onChange={(e) => setField({ doi: e.target.value })}
                className="w-full border border-[#0a0a0a] p-2 text-[14px] font-mono-tiny focus:outline-none focus:border-[#e34234]"
                data-testid="ref-doi-input"
              />
            </div>
            <div>
              <label htmlFor="ref-isbn" className="vbb-overline block mb-2">
                ISBN
              </label>
              <input
                id="ref-isbn"
                type="text"
                value={form.isbn}
                onChange={(e) => setField({ isbn: e.target.value })}
                className="w-full border border-[#0a0a0a] p-2 text-[14px] font-mono-tiny focus:outline-none focus:border-[#e34234]"
                data-testid="ref-isbn-input"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="ref-full-text"
              className="vbb-overline block mb-2"
            >
              Full text / archive (optional — paste the full article,
              transcript, or chapter here)
            </label>
            <textarea
              id="ref-full-text"
              value={form.fullText}
              onChange={(e) => setField({ fullText: e.target.value })}
              rows={12}
              className="w-full border border-[#0a0a0a] p-3 font-mono-tiny text-[13px] resize-y focus:outline-none focus:border-[#e34234]"
              placeholder="Paste the whole thing here so you have a captured copy if the source disappears. Markdown OK."
              data-testid="ref-full-text-input"
            />
            {form.fullText && (
              <p className="mt-1 font-mono-tiny text-[11px] text-[#6b6b6b]">
                {form.fullText.length.toLocaleString()} chars archived
              </p>
            )}
          </div>

          <div>
            <label htmlFor="ref-notes" className="vbb-overline block mb-2">
              Personal notes (markdown)
            </label>
            <textarea
              id="ref-notes"
              value={form.notes}
              onChange={(e) => setField({ notes: e.target.value })}
              rows={6}
              className="w-full border border-[#0a0a0a] p-3 font-mono-tiny text-[13px] resize-y focus:outline-none focus:border-[#e34234]"
              data-testid="ref-notes-input"
            />
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <section
            className="border border-[#0a0a0a] p-5"
            data-testid="ref-save-panel"
          >
            <div className="vbb-overline mb-3">Save</div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setField({ published: e.target.checked })}
                data-testid="ref-published-toggle"
                className="w-4 h-4"
              />
              <span className="text-[14px]">
                Recommended (surfaced in RSS as a recommended work)
              </span>
            </label>
            <div className="mt-5 flex items-center justify-between">
              <span
                className="font-mono-tiny text-[#6b6b6b]"
                data-testid="ref-save-state"
              >
                {savedState === "saving" && "Saving…"}
                {savedState === "saved" && "Saved."}
                {savedState === "error" && (
                  <span className="text-[#e34234]">Save failed.</span>
                )}
              </span>
              <button
                type="button"
                onClick={onSave}
                disabled={savedState === "saving" || !form.title.trim()}
                className="vbb-btn disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="ref-save-btn"
              >
                Save
              </button>
            </div>
          </section>

          {!isNew && (
            <section
              className="border border-[#0a0a0a] p-5 space-y-4"
              data-testid="ref-preview-panel"
            >
              <div className="vbb-overline">Live preview</div>
              <div>
                <div className="font-mono-tiny text-[#6b6b6b] uppercase mb-1">
                  APA 7
                </div>
                <p
                  className="text-[13px] leading-relaxed font-mono-tiny break-words"
                  data-testid="ref-preview-apa"
                >
                  {preview.apa || "…"}
                </p>
              </div>
              <div>
                <div className="font-mono-tiny text-[#6b6b6b] uppercase mb-1">
                  MLA 9
                </div>
                <p
                  className="text-[13px] leading-relaxed font-mono-tiny break-words"
                  data-testid="ref-preview-mla"
                >
                  {preview.mla || "…"}
                </p>
              </div>
              <p className="text-[11px] font-mono-tiny text-[#6b6b6b]">
                Preview refreshes after each save.
              </p>
            </section>
          )}
        </aside>
      </div>
    </main>
  );
}
